import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import { WotcSurveyData } from './types';
import { usePostWotcSurvey } from '@features/wotc/hooks/usePostWotcSurvey';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { staticFileLocation } from '@server/constants';

type vocationalRehabilitationFormModalProps = {
  vocationalRehabilitationFormModal: boolean;
  handleClose: () => void;
  wotcSurveyData: WotcSurveyData;
};
const defaultValues = {
  certification1: 'Y',
  certification1a: '',
};

const VocationalFormModal: FC<vocationalRehabilitationFormModalProps> = ({
  vocationalRehabilitationFormModal,
  wotcSurveyData,
  handleClose,
}) => {
  const [errorText, setErrorText] = useState('');
  const { sendWotcSurvey, isSendingWotcSurvey } = usePostWotcSurvey({
    setErrorText,
  });

  const { handleSubmit, control, watch, setValue, formState: { errors }, } = useForm({
    defaultValues,
  });

  const certification1 = watch('certification1');

  // Function to handle form submission
  const onSubmit = (data: { certification1: any; certification1a: any }) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        toast.success('Data submitted successfully');
        handleClose();
      },
    };

    const transformedData = {
      ...data,
      status: 7,
    };
    sendWotcSurvey({ ...transformedData }, options);
  };

  useEffect(() => {
    if (wotcSurveyData) {
      Object.keys(defaultValues).forEach((key) => {
        const typedKey = key as keyof typeof defaultValues &
          keyof WotcSurveyData;
        if (wotcSurveyData[typedKey] !== undefined) {
          if (wotcSurveyData[typedKey] !== null) {
            setValue(typedKey, wotcSurveyData[typedKey]);
          }
        }
      });
    }
  }, [wotcSurveyData, setValue]);

  return (
    <Dialog
      open={vocationalRehabilitationFormModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="10px"
        >
          <img
            src={`${staticFileLocation}img/images/icon8.png`}
            alt="Logo"
            css={css`
              max-height: 60px;
              display: block;
            `}
          />
          <Typography variant="h5">Conditional Certification</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {errorText && (
            <Alert sx={{ mb: 1 }} severity="error">
              {errorText}
            </Alert>
          )}
          {/* Question 1 */}
          <FormControl component="fieldset" fullWidth>
            <Box
              sx={{
                display: 'flex',
                alignItems: {
                  xs: 'start',
                  sm: 'center',
                },
                justifyContent: 'space-between',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                gap: '4px',
              }}
            >
              <FormLabel component="legend">
                Were you referred to an employer by state funded vocational or
                veteran's rehabilitation program?
              </FormLabel>
              <Controller
                name="certification1"
                control={control}
                rules={{ required: 'This field is required' }}
                render={({ field }) => (
                  <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                    <FormControlLabel
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          </FormControl>

          {/* Conditional Question 2 */}
          {certification1 === 'Y' && (
            <FormControl component="fieldset" fullWidth sx={{ mt: 3 }}>
              <FormLabel component="legend">
                Were you referred to an Employer by:
              </FormLabel>
              <Controller
                name="certification1a"
                control={control}
                rules={{
                  required:
                    certification1 === 'Y'
                      ? "You must select an option if 'Yes' is selected above"
                      : false,
                }}
                render={({ field }) => (
                  <RadioGroup {...field}>
                    <FormControlLabel
                      value="A Vocational Rehabilitation Agency approved by a state"
                      control={<Radio />}
                      label="A Vocational Rehabilitation Agency approved by a state"
                    />
                    <FormControlLabel
                      value="An Employment Network under the ticket to work program"
                      control={<Radio />}
                      label="An Employment Network under the ticket to work program"
                    />
                    <FormControlLabel
                      value="The department of veteran affairs"
                      control={<Radio />}
                      label="The department of veteran affairs"
                    />
                    <FormControlLabel
                      value="None of the above"
                      control={<Radio />}
                      label="None of the above"
                    />
                  </RadioGroup>
                )}
              />
               {errors.certification1a && (
                    <span
                      style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                    >
                      {errors.certification1a?.message}
                    </span>
                  )}
            </FormControl>
          )}

          {/* Submit Button */}
          <Box width="100%" textAlign="center">
            <LoadingButton
              loading={isSendingWotcSurvey}
              sx={{ width: '100%', mt: 3 }}
              type="submit"
              variant="contained"
              color="primary"
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VocationalFormModal;
